"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  advancedCanvasDropIndicatorsEqual,
  advancedCanvasSlotOverlaysEqual,
} from "./geometry";
import type {
  AdvancedCanvasDropIndicator,
  AdvancedCanvasSlotOverlay,
} from "./types";

export type AdvancedCanvasFeedback = {
  clearFeedback: () => void;
  dropIndicator: AdvancedCanvasDropIndicator | null;
  scheduleClearFeedback: () => void;
  scheduleDropIndicator: (
    nextIndicator: AdvancedCanvasDropIndicator | null,
  ) => void;
  scheduleSlotOverlays: (
    nextOverlays: AdvancedCanvasSlotOverlay[],
  ) => void;
  slotOverlays: AdvancedCanvasSlotOverlay[];
};

/**
 * Coalesces pointer-move feedback to one React update per animation frame.
 * Both builders use this exact scheduler so dense nested-slot drag operations
 * keep the same responsiveness and do not flicker between hit targets.
 */
export function useAdvancedCanvasFeedback(): AdvancedCanvasFeedback {
  const [dropIndicator, setDropIndicator] =
    useState<AdvancedCanvasDropIndicator | null>(null);
  const [slotOverlays, setSlotOverlays] =
    useState<AdvancedCanvasSlotOverlay[]>([]);
  const dropIndicatorFrameRef = useRef<number | null>(null);
  const pendingDropIndicatorRef =
    useRef<AdvancedCanvasDropIndicator | null | undefined>(undefined);
  const slotOverlaysFrameRef = useRef<number | null>(null);
  const pendingSlotOverlaysRef =
    useRef<AdvancedCanvasSlotOverlay[] | undefined>(undefined);

  const cancelPendingFeedback = useCallback(() => {
    if (dropIndicatorFrameRef.current !== null) {
      window.cancelAnimationFrame(dropIndicatorFrameRef.current);
      dropIndicatorFrameRef.current = null;
    }

    if (slotOverlaysFrameRef.current !== null) {
      window.cancelAnimationFrame(slotOverlaysFrameRef.current);
      slotOverlaysFrameRef.current = null;
    }

    pendingDropIndicatorRef.current = undefined;
    pendingSlotOverlaysRef.current = undefined;
  }, []);

  const scheduleDropIndicator = useCallback(
    (nextIndicator: AdvancedCanvasDropIndicator | null) => {
      pendingDropIndicatorRef.current = nextIndicator;

      if (dropIndicatorFrameRef.current !== null) {
        return;
      }

      dropIndicatorFrameRef.current = window.requestAnimationFrame(() => {
        const pendingIndicator = pendingDropIndicatorRef.current;

        dropIndicatorFrameRef.current = null;
        pendingDropIndicatorRef.current = undefined;

        if (pendingIndicator !== undefined) {
          setDropIndicator((current) => (
            advancedCanvasDropIndicatorsEqual(current, pendingIndicator)
              ? current
              : pendingIndicator
          ));
        }
      });
    },
    [],
  );

  const scheduleSlotOverlays = useCallback(
    (nextOverlays: AdvancedCanvasSlotOverlay[]) => {
      pendingSlotOverlaysRef.current = nextOverlays;

      if (slotOverlaysFrameRef.current !== null) {
        return;
      }

      slotOverlaysFrameRef.current = window.requestAnimationFrame(() => {
        const pendingOverlays = pendingSlotOverlaysRef.current;

        slotOverlaysFrameRef.current = null;
        pendingSlotOverlaysRef.current = undefined;

        if (pendingOverlays !== undefined) {
          setSlotOverlays((current) => (
            advancedCanvasSlotOverlaysEqual(current, pendingOverlays)
              ? current
              : pendingOverlays
          ));
        }
      });
    },
    [],
  );

  const clearFeedback = useCallback(() => {
    cancelPendingFeedback();
    setDropIndicator(null);
    setSlotOverlays([]);
  }, [cancelPendingFeedback]);

  const scheduleClearFeedback = useCallback(() => {
    scheduleDropIndicator(null);
    scheduleSlotOverlays([]);
  }, [scheduleDropIndicator, scheduleSlotOverlays]);

  useEffect(() => cancelPendingFeedback, [cancelPendingFeedback]);

  return {
    clearFeedback,
    dropIndicator,
    scheduleClearFeedback,
    scheduleDropIndicator,
    scheduleSlotOverlays,
    slotOverlays,
  };
}
